/**
 * The side panel of a training agent session (TRA-1327): what the session
 * runs with and the team-data tools its agent started with.
 */

import type { TrainingAgentSessionView } from '@/api/generated/model'

import { formatDateTime, formatUsd, shortTrainerRef } from './display'
import { toolLabel } from './transcript'

function Fact({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  )
}

function Budget({ session }: { session: TrainingAgentSessionView }): React.JSX.Element {
  const spent = Math.min(100, (session.cost_usd / session.budget_usd) * 100)
  return (
    <div className="space-y-1">
      <span className="font-mono text-xs">
        {formatUsd(session.cost_usd)} of {formatUsd(session.budget_usd)}
      </span>
      <div aria-hidden="true" className="h-1 bg-muted">
        <div className="h-1 bg-foreground" style={{ width: `${spent.toFixed(1)}%` }} />
      </div>
    </div>
  )
}

export function SessionDetails({
  session,
  tools,
}: {
  session: TrainingAgentSessionView
  tools: string[]
}): React.JSX.Element {
  return (
    <aside aria-label="Session details" className="hidden space-y-6 overflow-y-auto border-l border-border p-4 xl:block">
      <section className="space-y-3">
        <h3 className="font-serif text-base text-foreground">Session</h3>
        <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-baseline gap-x-3 gap-y-2 text-sm">
          <Fact label="Model">
            <span className="font-mono text-xs">{session.model}</span>
          </Fact>
          <Fact label="Trainer">
            <span className="font-mono text-xs" title={session.trainer_ref}>
              {shortTrainerRef(session.trainer_ref)}
            </span>
          </Fact>
          <Fact label="Budget">
            <Budget session={session} />
          </Fact>
          <Fact label="Opened by">{session.created_by}</Fact>
          <Fact label="Opened">{formatDateTime(session.created_at)}</Fact>
          {session.ended_at !== null && (
            <Fact label="Ended">{formatDateTime(session.ended_at)}</Fact>
          )}
        </dl>
      </section>
      <section className="space-y-3">
        <h3 className="font-serif text-base text-foreground">Team data tools</h3>
        {tools.length === 0 ? (
          <p className="text-sm text-muted-foreground">The agent lists its tools once it starts.</p>
        ) : (
          <ul className="space-y-1">
            {tools.map((tool) => (
              <li key={tool} className="font-mono text-xs">
                {toolLabel(tool)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  )
}
